/**
 * ReservationActionDialogs parts — EditDatesDialog and OverrideStatusDialog.
 *
 * Split out of `reservation-action-dialogs.tsx` to stay under the repo's
 * 400-line file cap (CU-86cb8grgm) — same split convention as
 * `inspect-popover-parts.tsx`. Props-only components, no data fetching.
 */

import * as React from 'react';
import { pl } from 'date-fns/locale';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { Label } from '@ooh/ui/components/label';
import { Textarea } from '@ooh/ui/components/textarea';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { DateRangeButtonField } from '@ooh/ui/components/date-range-button-field';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { AlertTriangle } from 'lucide-react';
import { guardClose } from './reservation-dialog-types';
import type { ReservationDialogLabels } from './reservation-dialog-types';
import { formatDisplayDateTime } from '@ooh/ui/lib/date-format';

// ─── Status options for override ─────────────────────────────

const OVERRIDE_STATUS_OPTIONS = [
  { value: 'SOFT_RESERVATION', label: 'Rezerwacja zwykła' },
  { value: 'HARD_RESERVATION', label: 'Rezerwacja twarda' },
  { value: 'SOLD', label: 'Sprzedany' },
  { value: 'OPTION', label: 'Stałka' },
];

/**
 * Tomorrow's date as a local YYYY-MM-DD string, used as the manual TTL
 * override's `minDate`. `ReservationsService.overrideStatus` rejects any
 * `ttlExpiresAt` at or before "now" (`isStaleManualTtl`, see
 * reservations.service.ts — "ttlExpiresAt must be a future date"). A
 * date-only string round-trips through UTC midnight once submitted
 * (`new Date('YYYY-MM-DD')`), so "today" is already in the past by that
 * check for virtually any pick made during normal working hours — disabling
 * it in the calendar avoids letting the user select a value the server will
 * always reject.
 */
function getTomorrowDateStr(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${day}`;
}

// ─── EditDatesDialog ─────────────────────────────────────────

export function EditDatesDialog({
  initialDateFrom,
  initialDateTo,
  isAdmin,
  labels,
  isPending = false,
  onConfirm,
  onClose,
}: {
  initialDateFrom: string;
  initialDateTo: string;
  isAdmin: boolean;
  labels: ReservationDialogLabels;
  isPending?: boolean;
  onConfirm: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [dateFrom, setDateFrom] = React.useState(initialDateFrom);
  const [dateTo, setDateTo] = React.useState(initialDateTo);
  const [force, setForce] = React.useState(false);

  const hasChange = dateFrom !== initialDateFrom || dateTo !== initialDateTo;

  return (
    <ModalDialog
      header={<span className="text-[15px] font-semibold">{labels.editDatesTitle}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            {labels.cancelBtnLabel}
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm({ dateFrom, dateTo, force })}
            disabled={!hasChange || isPending}
          >
            {labels.saveLabel}
          </Button>
        </div>
      }
      onClose={guardClose(onClose, isPending)}
    >
      <div className="space-y-4">
        <DateRangeButtonField
          fromValue={dateFrom}
          toValue={dateTo}
          onChange={({ from, to }) => {
            setDateFrom(from);
            setDateTo(to);
          }}
          label={labels.dateRangeLabel}
          testId="edit-dates-range"
          locale={pl}
        />
        {hasChange ? (
          <div className="flex items-start gap-2 rounded-lg bg-warning/10 border border-warning/30 px-3 py-2.5">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <p className="text-xs text-warning">{labels.displacementWarningLabel}</p>
          </div>
        ) : null}
        {isAdmin ? (
          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none">
            <Checkbox checked={force} onCheckedChange={(v) => setForce(v !== false)} />
            {labels.forceCheckboxLabel}
          </label>
        ) : null}
      </div>
    </ModalDialog>
  );
}

// ─── OverrideStatusDialog ─────────────────────────────────────

export function OverrideStatusDialog({
  labels,
  isPending = false,
  onConfirm,
  onClose,
}: {
  labels: ReservationDialogLabels;
  isPending?: boolean;
  onConfirm: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [targetStatus, setTargetStatus] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [ttlExpiresAt, setTtlExpiresAt] = React.useState('');
  const isValid = targetStatus !== '' && reason.trim().length > 0;
  // TTL override only applies to temp holds (SOFT/HARD) — SOLD/OPTION always
  // clear ttlExpiresAt server-side (see ReservationsService.overrideStatus).
  const showTtlField = targetStatus === 'SOFT_RESERVATION' || targetStatus === 'HARD_RESERVATION';

  return (
    <ModalDialog
      header={<span className="text-[15px] font-semibold">{labels.overrideStatusTitle}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            {labels.cancelBtnLabel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() =>
              onConfirm({
                targetStatus,
                reason,
                ttlExpiresAt: showTtlField && ttlExpiresAt !== '' ? ttlExpiresAt : undefined,
              })
            }
            disabled={!isValid || isPending}
          >
            {labels.overrideConfirmLabel}
          </Button>
        </div>
      }
      onClose={guardClose(onClose, isPending)}
    >
      <div className="space-y-4">
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            {labels.targetStatusLabel}
          </Label>
          <SearchableSelectField
            options={OVERRIDE_STATUS_OPTIONS}
            value={targetStatus}
            onChange={setTargetStatus}
            placeholder="Wybierz status..."
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            {labels.reasonLabel} <span className="text-destructive">*</span>
          </Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={labels.reasonPlaceholder}
            rows={3}
            className="text-[13px]"
          />
          {reason.trim().length === 0 ? (
            <p className="text-2xs text-destructive mt-1">{labels.reasonRequiredLabel}</p>
          ) : null}
        </div>
        {showTtlField ? (
          <div>
            <Label
              className="text-xs text-muted-foreground mb-1.5 block"
              htmlFor="override-ttl-input"
            >
              {labels.ttlExpiresAtLabel}
            </Label>
            <DatePickerButton
              id="override-ttl-input"
              testId="override-ttl-input"
              value={ttlExpiresAt}
              onChange={setTtlExpiresAt}
              placeholder={labels.ttlExpiresAtPlaceholder}
              minDate={getTomorrowDateStr()}
              locale={pl}
            />
            <p className="text-2xs text-muted-foreground mt-1">{labels.ttlExpiresAtHint}</p>
          </div>
        ) : null}
        <div className="rounded-lg bg-muted/40 border border-border/50 px-3 py-2.5 text-xs text-muted-foreground">
          {labels.auditNoteLabel}
        </div>
      </div>
    </ModalDialog>
  );
}

// ─── EditExpiryDeadlineDialog (CU-86cb7qgc9) ──────────────────

/**
 * Manual effective-expiry edit dialog: date-only picker + required reason.
 * `minDate` reuses `getTomorrowDateStr()` (same UX-affordance disabling
 * pattern as the override dialog's manual TTL field above) — the server's
 * locked re-read remains the authoritative future-date check.
 */
export function EditExpiryDeadlineDialog({
  currentDeadline,
  labels,
  isPending = false,
  onConfirm,
  onClose,
}: {
  currentDeadline: string | null;
  labels: ReservationDialogLabels;
  isPending?: boolean;
  onConfirm: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [expiryDate, setExpiryDate] = React.useState('');
  const [reason, setReason] = React.useState('');
  const isValid = expiryDate !== '' && reason.trim().length > 0;

  return (
    <ModalDialog
      header={<span className="text-sm font-semibold">{labels.extendExpiryTitle}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            {labels.cancelBtnLabel}
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm({ expiryDate, reason: reason.trim() })}
            disabled={!isValid || isPending}
          >
            {labels.extendExpirySaveLabel}
          </Button>
        </div>
      }
      onClose={guardClose(onClose, isPending)}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{labels.extendExpiryDescription}</p>
        {currentDeadline !== null ? (
          <div className="flex items-baseline justify-between gap-3 rounded-lg border border-border/50 bg-muted/30 px-3 py-2">
            <p className="text-2xs font-medium text-muted-foreground">
              {labels.extendExpiryCurrentLabel}
            </p>
            <p className="shrink-0 text-sm font-medium">{formatDisplayDateTime(currentDeadline)}</p>
          </div>
        ) : null}
        <div>
          <Label
            className="text-xs text-muted-foreground mb-1.5 block"
            htmlFor="expiry-deadline-input"
          >
            {labels.extendExpiryNewLabel}
          </Label>
          <DatePickerButton
            id="expiry-deadline-input"
            testId="expiry-deadline-input"
            value={expiryDate}
            onChange={setExpiryDate}
            placeholder={labels.extendExpiryDatePlaceholder}
            minDate={getTomorrowDateStr()}
            locale={pl}
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            {labels.extendExpiryReasonLabel} <span className="text-destructive">*</span>
          </Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={labels.extendExpiryReasonPlaceholder}
            rows={3}
            className="text-sm"
          />
          {reason.trim().length === 0 ? (
            <p className="text-2xs text-destructive mt-1">
              {labels.extendExpiryReasonRequiredLabel}
            </p>
          ) : null}
        </div>
      </div>
    </ModalDialog>
  );
}
